import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { useMerch } from '../../contexts/MerchContext';
import CartDrawer from './CartDrawer';
import { ShoppingBag, ArrowLeft, Loader, Check } from 'lucide-react';

const ProductDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [variants, setVariants] = useState([]);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [loading, setLoading] = useState(true);
    const { addToCart, setIsCartOpen, cartCount } = useMerch();

    useEffect(() => {
        fetchProductDetails();
    }, [id]);

    const fetchProductDetails = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/merch-api/products/${id}`;

            const res = await fetch(functionUrl, {
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`,
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                }
            });
            const result = await res.json();

            if (result.result) {
                // Printful v2 structure for single product: result: { sync_product: {...}, sync_variants: [...] }
                const productData = result.result.sync_product;
                const variantsData = result.result.sync_variants;

                setProduct({
                    ...productData,
                    // Map thumbnail if needed, though Printful usually gives 'thumbnail_url' in productData
                });

                // Map variants to useful structure if needed
                setVariants(variantsData);

                // Select first variant if available
                if (variantsData && variantsData.length > 0) {
                    setSelectedVariant(variantsData[0]);
                }
            }
        } catch (error) {
            console.error('Error fetching product details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = () => {
        if (!selectedVariant || !product) return;
        addToCart(product, selectedVariant, 1);
    };

    if (loading) return (
        <div className="min-h-screen bg-background pt-20 flex justify-center items-center">
            <Loader className="w-8 h-8 text-primary animate-spin" />
        </div>
    );

    if (!product) return <div>Product not found</div>;

    return (
        <div className="min-h-screen bg-background pt-20 pb-12">
            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                    <button onClick={() => navigate('/merch')} className="flex items-center text-muted-foreground hover:text-foreground transition-colors">
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Back to Store
                    </button>
                    <button
                        onClick={() => setIsCartOpen(true)}
                        className="relative p-3 bg-card border border-border rounded-full hover:bg-muted transition-colors"
                    >
                        <ShoppingBag className="w-6 h-6 text-foreground" />
                        {cartCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
                                {cartCount}
                            </span>
                        )}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    {/* Image */}
                    <div className="bg-card border border-border rounded-2xl p-8 flex items-center justify-center">
                        <img
                            src={selectedVariant?.image || product.image || product.thumbnail_url}
                            alt={product.name}
                            className="max-w-full max-h-[500px] object-contain drop-shadow-2xl"
                        />
                    </div>

                    {/* Info */}
                    <div>
                        <h1 className="text-4xl font-bold text-foreground mb-4">{product.name}</h1>
                        <div className="text-2xl font-bold text-primary mb-6">
                            ${selectedVariant ? selectedVariant.retail_price : '...'}
                        </div>

                        {/* Variant Selector (Simple dropdown for now, can be sophisticated later) */}
                        <div className="mb-8">
                            <label className="block text-sm font-medium text-muted-foreground mb-2">Select Option</label>
                            <div className="flex flex-wrap gap-2">
                                {variants.map(v => (
                                    <button
                                        key={v.id}
                                        onClick={() => setSelectedVariant(v)}
                                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${selectedVariant?.id === v.id
                                            ? 'bg-primary text-primary-foreground border-primary'
                                            : 'bg-card text-foreground border-border hover:border-primary'
                                            }`}
                                    >
                                        {v.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={handleAddToCart}
                            className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-bold text-lg hover:bg-primary/90 transition-all active:scale-[0.98] flex items-center justify-center"
                        >
                            <ShoppingBag className="w-5 h-5 mr-2" />
                            Add to Cart
                        </button>

                        <div className="mt-8 prose prose-invert text-muted-foreground">
                            <h3 className="text-foreground">Description</h3>
                            {/* Product description is purely text in Printful usually? */}
                            <p>High quality gaming merchandise featuring exclusive Solis Games designs.</p>
                        </div>
                    </div>
                </div>
            </div>
            <CartDrawer />
        </div>
    );
};

export default ProductDetails;
